import { watch } from 'vue';
import { CUSTOM_STYLE_INJECTION_KEYS } from '@/shared/constants/custom-styles';
import { getHelixApiBaseUrl } from '@/utils/api-url';
import { publicSettings } from '@/composables/use-public-settings';
import {
  applyFaviconUrl,
  applyLoginBackgroundUrl,
  injectStylesheetLink,
  resolveCustomCssUrl,
  resolveCustomFaviconUrl,
  resolveCustomResourceUrl,
} from '@/utils/custom-style-resources';
import { applyClientCodeStyleLayers, normalizeClientCode } from '@/composables/client-code-styles';
import { useOSettings } from '@/composables/use-settings';

export type CustomStyleSourceValues = {
  custom_css?: unknown;
  custom_favicon?: unknown;
  custom_login_background?: unknown;
  app_client_code?: unknown;
};

/** Treat null/undefined/blank as unset so public bootstrap can fill the gap. */
function coalesceStyleValue(authenticated: unknown, publicValue: unknown): unknown {
  if (authenticated == null) {
    return publicValue;
  }

  if (typeof authenticated === 'string' && authenticated.trim() === '') {
    return publicValue;
  }

  return authenticated;
}

/**
 * Authenticated settings override public bootstrap only when defined.
 * Prevents an empty authenticated snapshot from wiping API public CSS.
 * Empty strings are treated as unset (API often sends "" for missing values).
 */
export function mergeCustomStyleSources(
  authenticatedValues: CustomStyleSourceValues = {},
  publicValues: CustomStyleSourceValues = {},
): CustomStyleSourceValues {
  return {
    custom_css: coalesceStyleValue(authenticatedValues.custom_css, publicValues.custom_css),
    custom_favicon: coalesceStyleValue(authenticatedValues.custom_favicon, publicValues.custom_favicon),
    custom_login_background: coalesceStyleValue(authenticatedValues.custom_login_background, publicValues.custom_login_background),
    app_client_code: coalesceStyleValue(authenticatedValues.app_client_code, publicValues.app_client_code),
  };
}

function readPublicStyleValues(): CustomStyleSourceValues {
  return {
    custom_css: publicSettings.custom_css,
    custom_favicon: publicSettings.custom_favicon ?? publicSettings.customFavicon,
    custom_login_background: publicSettings.custom_login_background ?? publicSettings.customLoginBackground,
    app_client_code: publicSettings.app_client_code,
  };
}

function applyRemoteOnlyCustomResources(settings: Record<string, unknown>, { apiBaseUrl = getHelixApiBaseUrl() } = {}) {
  const { CUSTOM_CSS } = CUSTOM_STYLE_INJECTION_KEYS;

  injectStylesheetLink(CUSTOM_CSS, resolveCustomCssUrl(apiBaseUrl, settings?.custom_css), {
    dataset: { customStyleSource: 'remote' },
    bustCache: true,
  });
}

function applySharedCustomResources(settings: Record<string, unknown>, { apiBaseUrl = getHelixApiBaseUrl() } = {}) {
  applyFaviconUrl(resolveCustomFaviconUrl(apiBaseUrl, settings?.custom_favicon));
  applyLoginBackgroundUrl(resolveCustomResourceUrl(apiBaseUrl, settings?.custom_login_background));
}

async function applyAllCustomStyles(settings: Record<string, unknown>, { apiBaseUrl = getHelixApiBaseUrl() } = {}) {
  applySharedCustomResources(settings, { apiBaseUrl });

  const clientCode = settings?.app_client_code;

  if (normalizeClientCode(clientCode)) {
    await applyClientCodeStyleLayers({
      clientCode,
      customCss: settings?.custom_css,
      apiBaseUrl,
    });
    return;
  }

  applyRemoteOnlyCustomResources(settings, { apiBaseUrl });
}

export function useCustomStyles() {
  const settings = useOSettings();

  function readAuthenticatedStyleValues(): CustomStyleSourceValues {
    return {
      custom_css: settings.custom_css?.value,
      custom_favicon: settings.custom_favicon?.value,
      custom_login_background: settings.custom_login_background?.value,
      app_client_code: settings.app_client_code?.value,
    };
  }

  function applyMergedCustomStyles() {
    applyAllCustomStyles(mergeCustomStyleSources(readAuthenticatedStyleValues(), readPublicStyleValues()) as Record<string, unknown>);
  }

  watch(() => readPublicStyleValues(), applyMergedCustomStyles, { immediate: true, deep: true });
  watch(() => readAuthenticatedStyleValues(), applyMergedCustomStyles, { immediate: true, deep: true });
}

export function applyPublicCustomStyles(apiBaseUrl: unknown, rawPublicSettings: Record<string, unknown> = publicSettings) {
  return applyAllCustomStyles(rawPublicSettings, { apiBaseUrl: apiBaseUrl as string | null });
}
